import { useApolloClient } from "@apollo/client";
import { Dropdown, Input, Modal, Spin } from "antd";
import { get, isEmpty, isNil } from "lodash";
import moment from "moment";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronDown, BiChevronRight } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  SUBSCRIPTION_CLIENT_OPTIONS,
  SUBSCRIPTIONS_LIST,
} from "utils/api/graphql/queries/subscription";
import { SAMPLE_PAYLOADS } from "../../sample-payloads";

export const TEST_DATA_KINDS = {
  SAMPLE: "sample",
  REAL: "real",
};

const REAL_PROJECT_KEY = "REAL_PROJECT";
const SEARCH_LIMIT = 10;
const ITEMS_LIMIT = 20;
const NUMBER_LIMIT = 5;
const DAY_FORMAT = "DD/MM/YYYY";
const NUMBER_PATTERN = /^\d+$/;
const ITEM_KINDS = { SUBSCRIPTION: "subscription", QUOTE: "quote" };
const STATUSES = {
  IDLE: "idle",
  LOADING: "loading",
  DONE: "done",
  FAILED: "failed",
};
const PROJECT_EXCLUDE = [
  "subscriptions",
  "contract",
  "process",
  "processStatuses",
  "flows",
  "tracers",
  "possibleStatuses",
  "conditions",
  "actions",
  "actionsConditions",
];

const day = (date) => (isNil(date) ? "" : moment(date).format(DAY_FORMAT));

const clientName = (user) =>
  [get(user, "firstname"), get(user, "lastname")]
    .filter((part) => !isEmpty(part))
    .join(" ");

const projectsVariables = (term) => ({
  take: SEARCH_LIMIT,
  skip: 0,
  withSubscriptions: false,
  withContract: false,
  withProcess: false,
  withTracers: false,
  withFlows: false,
  withProcessStatuses: false,
  exclude: PROJECT_EXCLUDE,
  like: {
    AND: term
      .split(/\s+/)
      .filter((word) => !isEmpty(word))
      .map((word) => ({
        OR: [
          { contact: { user: { firstname: `%${word}%` } } },
          { contact: { user: { lastname: `%${word}%` } } },
        ],
      })),
  },
});

const RealProjectModal = ({ open, onClose, onPick }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const TEST_DATA = t("TEST_DATA", { returnObjects: true });
  const client = useApolloClient();
  const [search, setSearch] = useState({ status: STATUSES.IDLE });
  const [expanded, setExpanded] = useState({});
  const [picking, setPicking] = useState(null);
  const [pickFailed, setPickFailed] = useState(false);
  const searchRef = useRef(0);

  const query = (document, variables, path) =>
    client
      .query({ query: document, variables, fetchPolicy: "network-only" })
      .then((result) => get(result, path) || []);

  const subscriptionItem = (subscription) => ({
    kind: ITEM_KINDS.SUBSCRIPTION,
    id: subscription.id,
    label: t("TEST_DATA.SUBSCRIPTION", {
      plan: get(subscription, "insurancePlan.name") ?? "",
      date: day(subscription.createdDate),
    }),
  });

  const quoteItem = (quote) => ({
    kind: ITEM_KINDS.QUOTE,
    id: quote.id,
    label: t("TEST_DATA.QUOTE", { date: day(quote.createdDate) }),
  });

  const runSearch = async (text) => {
    const term = (text || "").trim();
    if (isEmpty(term)) return;
    searchRef.current += 1;
    const call = searchRef.current;
    setSearch({ status: STATUSES.LOADING });
    setExpanded({});
    setPickFailed(false);
    const isNumber = NUMBER_PATTERN.test(term);
    try {
      const [projects, subscriptions, quotes] = await Promise.all([
        isNumber
          ? []
          : query(
              graphql.queries.PROJECTS_LIST,
              projectsVariables(term),
              "data.projectsList.data",
            ),
        isNumber
          ? query(
              SUBSCRIPTION_CLIENT_OPTIONS,
              { where: { number: Number(term) }, take: NUMBER_LIMIT },
              "data.subscriptions.data",
            )
          : [],
        isNumber
          ? query(
              graphql.queries.QUOTES,
              { where: { number: Number(term) }, take: NUMBER_LIMIT, skip: 0 },
              "data.quotes.data",
            )
          : [],
      ]);
      if (call !== searchRef.current) return;
      setSearch({
        status: STATUSES.DONE,
        projects,
        items: [
          ...subscriptions.map((subscription) => ({
            kind: ITEM_KINDS.SUBSCRIPTION,
            id: subscription.id,
            label: t("TEST_DATA.SUBSCRIPTION_NUMBER", {
              number: term,
              client:
                clientName(get(subscription, "project.contact.user")) ||
                TEST_DATA.UNKNOWN_CLIENT,
            }),
          })),
          ...quotes.map((quote) => ({
            kind: ITEM_KINDS.QUOTE,
            id: quote.id,
            label: t("TEST_DATA.QUOTE_NUMBER", {
              number: term,
              date: day(quote.createdDate),
            }),
          })),
        ],
      });
    } catch (_error) {
      if (call !== searchRef.current) return;
      setSearch({ status: STATUSES.FAILED });
    }
  };

  const expand = async (project) => {
    if (!isNil(expanded[project.id])) {
      setExpanded((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([id]) => id !== project.id),
        ),
      );
      return;
    }
    setExpanded((current) => ({
      ...current,
      [project.id]: { status: STATUSES.LOADING, items: [] },
    }));
    const where = { project: { id: project.id } };
    const next = await Promise.all([
      query(
        SUBSCRIPTIONS_LIST,
        { where, take: ITEMS_LIMIT, skip: 0 },
        "data.subscriptionsList.data",
      ),
      query(
        graphql.queries.QUOTES,
        { where, take: ITEMS_LIMIT, skip: 0 },
        "data.quotes.data",
      ),
    ])
      .then(([subscriptions, quotes]) => ({
        status: STATUSES.DONE,
        items: [
          ...subscriptions.map(subscriptionItem),
          ...quotes.map(quoteItem),
        ],
      }))
      .catch(() => ({ status: STATUSES.FAILED, items: [] }));
    setExpanded((current) =>
      isNil(current[project.id]) ? current : { ...current, [project.id]: next },
    );
  };

  const loadSchema = (variables) =>
    client
      .query({
        query: graphql.queries.DOCUMENT_PAYLOAD_SCHEMA,
        variables,
        fetchPolicy: "network-only",
      })
      .then((result) => get(result, "data.documentPayloadSchema"))
      .catch(() => null);

  const pick = async (item, owner) => {
    setPicking(item.id);
    setPickFailed(false);
    const schema = await loadSchema(
      item.kind === ITEM_KINDS.QUOTE
        ? { quote: { id: item.id } }
        : { where: { id: item.id } },
    );
    setPicking(null);
    if (isNil(get(schema, "payload"))) {
      setPickFailed(true);
      return;
    }
    onPick({
      label: isEmpty(owner) ? item.label : `${owner} · ${item.label}`,
      payload: schema.payload,
      paths: schema.paths || [],
    });
    onClose();
  };

  const itemButton = (item, owner) => (
    <button
      type="button"
      key={`${item.kind}-${item.id}`}
      className="dynamic-documents-editor__test-item"
      disabled={!isNil(picking)}
      onClick={() => pick(item, owner)}
    >
      {item.label}
      {picking === item.id && <Spin size="small" />}
    </button>
  );

  const projects = get(search, "projects") || [];
  const items = get(search, "items") || [];

  return (
    <Modal
      open={open}
      centered
      footer={null}
      title={TEST_DATA.TITLE}
      onCancel={onClose}
      destroyOnClose
    >
      <div className="dynamic-documents-editor__test">
        <Input.Search
          autoFocus
          allowClear
          placeholder={TEST_DATA.SEARCH}
          loading={search.status === STATUSES.LOADING}
          onSearch={runSearch}
        />
        <p className="dynamic-documents-editor__muted">{TEST_DATA.HINT}</p>
        {pickFailed && (
          <p className="dynamic-documents-editor__error">
            {TEST_DATA.LOAD_FAILED}
          </p>
        )}
        {search.status === STATUSES.FAILED && (
          <p className="dynamic-documents-editor__error">
            {TEST_DATA.SEARCH_FAILED}
          </p>
        )}
        {search.status === STATUSES.DONE &&
          isEmpty(projects) &&
          isEmpty(items) && (
            <p className="dynamic-documents-editor__muted">
              {TEST_DATA.NO_RESULT}
            </p>
          )}
        <div className="dynamic-documents-editor__test-results">
          {items.map((item) => itemButton(item, ""))}
          {projects.map((project) => {
            const owner =
              clientName(get(project, "contact.user")) ||
              TEST_DATA.UNKNOWN_CLIENT;
            const state = expanded[project.id];
            return (
              <div
                key={project.id}
                className="dynamic-documents-editor__test-project"
              >
                <button
                  type="button"
                  className="dynamic-documents-editor__test-project-head"
                  onClick={() => expand(project)}
                >
                  {isNil(state) ? <BiChevronRight /> : <BiChevronDown />}
                  {t("TEST_DATA.PROJECT", {
                    client: owner,
                    date: day(project.createdDate),
                  })}
                </button>
                {get(state, "status") === STATUSES.LOADING && (
                  <Spin size="small" />
                )}
                {get(state, "status") === STATUSES.FAILED && (
                  <p className="dynamic-documents-editor__error">
                    {TEST_DATA.SEARCH_FAILED}
                  </p>
                )}
                {get(state, "status") === STATUSES.DONE &&
                  (isEmpty(state.items) ? (
                    <p className="dynamic-documents-editor__muted">
                      {TEST_DATA.NOTHING_TO_TEST}
                    </p>
                  ) : (
                    <div className="dynamic-documents-editor__test-items">
                      {state.items.map((item) => itemButton(item, owner))}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

const TestDataPicker = ({
  selection,
  onSelectSample,
  onSelectReal,
  realOpen,
  onRealOpenChange,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const PREVIEW = t("PREVIEW", { returnObjects: true });

  const items = [
    {
      type: "group",
      label: PREVIEW.SAMPLES,
      children: SAMPLE_PAYLOADS.map(({ key, label }) => ({ key, label })),
    },
    { type: "divider" },
    { key: REAL_PROJECT_KEY, label: PREVIEW.REAL_PROJECT },
  ];

  const onClick = ({ key }) => {
    if (key === REAL_PROJECT_KEY) {
      onRealOpenChange(true);
      return;
    }
    onSelectSample(key);
  };

  return (
    <span className="dynamic-documents-editor__test-with">
      <span className="dynamic-documents-editor__test-with-label">
        {PREVIEW.TEST_WITH}
      </span>
      <Dropdown menu={{ items, onClick }} trigger={["click"]}>
        <button
          type="button"
          className="dynamic-documents-editor__btn dynamic-documents-editor__btn--sm"
        >
          <span className="dynamic-documents-editor__test-with-value">
            {selection.label}
          </span>
          <BiChevronDown size={16} />
        </button>
      </Dropdown>
      <RealProjectModal
        open={realOpen}
        onClose={() => onRealOpenChange(false)}
        onPick={onSelectReal}
      />
    </span>
  );
};

export default TestDataPicker;
